import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MyPlaceResponse } from '../../../../components/RoutingResult/types';
import useThemeContext from '../../../../contexts/theme';
import useApiCall from '../../../../hooks/useApiCall';
import useErrors from '../../../../hooks/useErrors';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import companyTokenService from '../../../../services/companyTokenService';
import ConsultsService from '../../../../services/ConsultsService';
import myPlaceService from '../../../../services/myPlaceService';
import floatToCurrency from '../../../../utils/floatToCurrency';
import formatCpf from '../../../../utils/formatCpf';
import formatCurrency from '../../../../utils/formatCurrency';
import formatPhone from '../../../../utils/formatPhone';
import isCpfvalid from '../../../../utils/isCpfValid';
import isEmailValid from '../../../../utils/isEmailValid';
import isValueValid from '../../../../utils/isValueValid';
import onlyNumbers from '../../../../utils/onlyNumbers';
import parseCurrencyStringToFloat from '../../../../utils/parseCurrencyStringToFloat';
import { ConsultTypeType, DadosConsulta, EnderecosLt, GetResultApiResponse, IAsksGoToExistingResultModalData, IWorkplacesOptions } from '../types';

export default function useNewConsult() {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [consultType, setConsultType] = useState<ConsultTypeType>('default');

  const [workplaces, setWorkplaces] = useState<EnderecosLt[]>([]);
  const [newConsultData, setNewConsultData] = useState({} as DadosConsulta);

  const [selectedWalkDistance, setSelectedWalkDistance] = useState([500, 500]);
  const [, setWalkDistance] = useState([500, 500]);
  const [canChooseUsesAffreightment, setCanChoseUsesAffreightment] = useState(true);
  const { userProfile, userCode, userName, companyCode, companyName } = useGetGeneralAppData();

  const [selectedWorkplaces, setSelectedWorkplaces] = useState<IWorkplacesOptions[]>([]);
  const [routeName, setRouteName] = useState('');

  const [isGettingMyPlace, setIsGettingMyPlace] = useState(false);
  const [asksGoToExistingResultModalData, setAsksGoToExistingResultModalData] = useState({} as IAsksGoToExistingResultModalData);

  const correspondingWorkplace = useMemo(() => (workplaces.find((wp) => wp.codEndA === newConsultData.codEndA)), [newConsultData.codEndA, workplaces]);
  const valueLabelByCorrespondingWorkplace: IWorkplacesOptions = useMemo(() => (
    { value: correspondingWorkplace?.codEndA ?? 0, label: correspondingWorkplace?.denominacaoLT ? `${correspondingWorkplace?.denominacaoLT} ${correspondingWorkplace?.distanceFromEmployeeHome ? `(${(correspondingWorkplace?.distanceFromEmployeeHome / 1000).toFixed(2)}km)` : ''}` : '', ...correspondingWorkplace } as IWorkplacesOptions
  ), [correspondingWorkplace]);

  const workplacesOptions: IWorkplacesOptions[] = useMemo(() => {
    const { uf } = newConsultData;

    const exceptions = [['GO', 'DF'], ['GO', 'TO'], ['MA', 'PI'], ['PE', 'BA'], ['SP', 'MG'], ['MG', 'RJ'], ['SP', 'RJ'], ['PR', 'SC']];
    const exceptionGroupsOfUf = exceptions.filter((exception) => exception.includes(uf));

    const filteredWorkplaces = workplaces.filter((wp) => {
      if (!uf) return true;

      if (!exceptionGroupsOfUf || exceptionGroupsOfUf.length === 0) {
        return wp.UF === uf;
      }

      return exceptionGroupsOfUf.some((exception) => exception.includes(wp.UF));
    });

    const mappedWorkplaces = filteredWorkplaces?.map((wp) => ({ value: wp?.codEndA, label: `${wp?.denominacaoLT} ${wp?.distanceFromEmployeeHome ? `(${(wp?.distanceFromEmployeeHome / 1000).toFixed(2)}km)` : ''}`, ...wp }));

    return mappedWorkplaces || [];
  }, [newConsultData, workplaces]);

  const navigate = useNavigate();

  const {
    setError, removeError, getErrorMessageByFieldName, errors
  } = useErrors();
  const { selectedTheme } = useThemeContext();
  const { handleSelectedCompanyChange, services, isCustomer } = useGetGeneralAppData();
  const { apiCall } = useApiCall();

  const hasNewCoordsSettled = useMemo(() => (
    newConsultData.lat && newConsultData.lng
  ), [newConsultData.lat, newConsultData.lng]);

  const newCoords = useMemo(() => (
    hasNewCoordsSettled ? (`${newConsultData.lat}, ${newConsultData.lng}`) : ''
  ), [hasNewCoordsSettled, newConsultData.lat, newConsultData.lng]);

  const hasMyPlace = useMemo(() => !!((services?.some((x) => x.codServ === 17) && consultType === 'default') || (!isCustomer && consultType === 'default')), [services, consultType, isCustomer]);

  const getDataToCreateConsult = useCallback(async () => {
    await apiCall({
      apiToCall: ConsultsService.getDataToCreateConsult,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        cpf: 0,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível carregar os dados para incluir uma nova consulta. Por favor, tente novamente',
      catchAction: () => setHasError(true),
      actionAfterResponse: (response: GetResultApiResponse) => {
        if (!response.codigo) {
          setHasError(true);
          toast.error(`Não foi possível carregar os dados para incluir uma nova consulta. Por favor, tente novamente (${response.msg})`);
          return;
        }
        setHasError(false);

        const { dadosConfiguracao } = response;
        setWorkplaces(response.enderecosLT ?? []);
        setCanChoseUsesAffreightment(dadosConfiguracao.usaFreta);
        setNewConsultData((prevState) => ({
          ...prevState,
          usaEspelhamento: dadosConfiguracao.espelhamento,
          usaFretado: dadosConfiguracao.usaFreta,
          distancia: dadosConfiguracao.distInicial,
          usaTeto: dadosConfiguracao.utilizaTeto,
          valorTeto: floatToCurrency (dadosConfiguracao?.valorTeto) ?? 0,
          valorVT: floatToCurrency(dadosConfiguracao?.valVTInicial) ?? 0,
          semTaxaVT: dadosConfiguracao.regraVTBilSemTx,
          regraVTSemTxAesa: dadosConfiguracao.regraVTSemTxAesa,
          semIntegraVT: dadosConfiguracao.regraBilSemIntegraRJ,
          salario: dadosConfiguracao.regraBilSemIntegraRJ ? 5000 : 2200,
          codFuncionario: 0,
          codConsulta: 0,
          cpf: '',
          matricula: '',
          nome: '',
          mail: '',
          diasVT: 30,
          cep: '',
          logradouro: '',
          num: '',
          complemento: '',
          bairro: '',
          cidade: '',
          uf: '',
          codEndA: 0,
          horaEntra: '08:00',
          horaSai: '18:00',
          valorVTIdeal: 0,
          statusCod: 0,
          statusRoteirizacao: '',
          dataProcessamento: '',
          lat: '',
          lng: '',
          statusConsulta: '',
          dataImplantado: '',
          implantado: 0,
          opcaoVTFreta: false,
          compararVT: 0,
          statusTeto: '',
          celular: '',
        }));
        setSelectedWalkDistance([500, dadosConfiguracao.distInicial]);
      }
    });
  }, [apiCall, companyCode, userCode, userProfile]);

  const getEmployeeDataByCpf = useCallback(async (cpf: string) => {
    await apiCall({
      apiToCall: ConsultsService.getDataToCreateConsult,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        cpf,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response: GetResultApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível encontrar um pré-cadastro deste CPF. (${response.msg})`);
          return;
        }

        const { dadosFunc, ultimaConsultaValida, codUltimaConsulta, ultimaConsultaMr } = response;

        if (ultimaConsultaValida && codUltimaConsulta) {
          if (!(!ultimaConsultaMr && consultType === 'default')) {
            setAsksGoToExistingResultModalData({ show: true, link: `/consults/result/${codUltimaConsulta}?codEmpresa=${companyCode}&activeSidebar=NewConsult&returnLink=${encodeURIComponent('/consults/new')}${ultimaConsultaMr ? '&mr' : ''}`});
            return;
          }

          toast.info('Este CPF já possui uma consulta, portanto, redirecionamos para o resultado da mesma.');
          navigate(`/consults/result/${codUltimaConsulta}?codEmpresa=${companyCode}&activeSidebar=NewConsult&returnLink=${encodeURIComponent('/consults/new')}${ultimaConsultaMr ? '&mr' : ''}`);
          return;
        }

        if (!dadosFunc) {
          return;
        }

        setNewConsultData((prevState) => ({
          ...prevState,
          matricula: dadosFunc.matricula ?? '',
          nome: dadosFunc.nome ?? '',
          mail: dadosFunc.mail ?? '',
          cep: dadosFunc.cep ?? '',
          logradouro: dadosFunc.logradouro ?? '',
          num: dadosFunc.num ?? '',
          bairro: dadosFunc.bairro ?? '',
          cidade: dadosFunc.cidade ?? '',
          uf: dadosFunc.uf ?? '',
          lat: dadosFunc.lat ?? '',
          lng: dadosFunc.lng ?? '',
          codEndA: dadosFunc.codEndA,
          codFuncionario: dadosFunc.codFuncionario,
        }));
      }
    });
  }, [apiCall, companyCode, consultType, navigate, userCode, userProfile]);

  const getMyPlace = useCallback(async () => {
    const { logradouro, num, cep, bairro, cidade, uf, lat, lng } = newConsultData;
    const isFullAddressFilled = logradouro && num && cep && bairro && cidade && uf && lat && lng;
    if (!hasMyPlace || !isFullAddressFilled) return;

    try {
      setIsGettingMyPlace(true);
      let token = '';
      await apiCall({
        apiToCall: companyTokenService.getCompanyToken,
        queryParams: { companyId: String(companyCode) },
        actionAfterResponse: (response) => {
          token = response.token || '';
        },
        catchMessage: 'Houve um erro ao carregar o token da empresa',
        catchAction() {
          token = '';
        },
      });

      if (!token) return;

      await apiCall({
        apiToCall: myPlaceService.getMyPlaceData,
        queryParams: { token },
        reqBody: JSON.stringify({
          streetName: newConsultData.logradouro,
          streetNumber: newConsultData.num,
          zipCode: newConsultData.cep,
          district: newConsultData.bairro,
          city: newConsultData.cidade,
          uf: newConsultData.uf,
          desirableDistance: 30000,
        }),
        actionAfterResponse: (response: MyPlaceResponse) => {
          if (!response.success) return;

          const admissionRadarWorkplaces = response.admissionRadarWorkplaces;

          setWorkplaces((prevWorkplaces) => {
            const mappedWorkplaces = admissionRadarWorkplaces.map((workplace) => {
              const wp = prevWorkplaces.find((wp) => wp.codEnd === workplace.captaId);
              return {
                ...wp,
                distanceFromEmployeeHome: workplace.distance,
              } as EnderecosLt;
            }).filter((wp) => wp.codEnd !== undefined);

            const missingWorkplaces = prevWorkplaces.filter((wp) => !admissionRadarWorkplaces.some((workplace) => workplace.captaId === wp.codEnd));
            return [...mappedWorkplaces, ...missingWorkplaces];
          });
        },
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsGettingMyPlace(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, companyCode, correspondingWorkplace?.codEnd, hasMyPlace, newConsultData.bairro, newConsultData.cep, newConsultData.cidade, newConsultData.logradouro, newConsultData.num, newConsultData.uf, newConsultData.lat, newConsultData.lng]);

  useEffect(() => {
    getMyPlace();
  }, [getMyPlace]);

  useEffect(() => {
    if (companyCode) {
      getDataToCreateConsult();
    }
  }, [companyCode, getDataToCreateConsult]);

  function handleConsultTypeChange(type: ConsultTypeType) {
    setConsultType(type);

    if (type === 'multi') {
      toast.info('Na seleção de locais de trabalho será possível selecionar vários. Selecione na ordem correta, sabendo que a volta será calculada do último local de trabalho selecionado, para o endereço do funcionário.', { toastId: 'consultTypeChange', autoClose: false });
    } else {
      toast.dismiss('consultTypeChange');
    }
  }

  function handleTryAgain() {
    getDataToCreateConsult();
  }

  function handleCpfChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      cpf: formatCpf(event.target.value)
    }));

    if (!isCpfvalid(formatCpf(event.target.value))) {
      setError({ field: 'cpf', message: 'CPF inválido' });
    } else {
      removeError('cpf');
      getEmployeeDataByCpf(formatCpf(event.target.value));
    }
  }

  function handleRegistrationChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      matricula: event.target.value
    }));
  }

  function handleNameChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      nome: event.target.value
    }));

    if (!event.target.value) {
      setError({ field: 'name', message: 'Nome é obrigatório!' });
    } else {
      removeError('name');
    }
  }

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      mail: event.target.value
    }));

    if (!event.target.value) {
      setError({ field: 'email', message: 'E-mail é obrigatório!' });
    } else if (!isEmailValid(event.target.value)) {
      setError({ field: 'email', message: 'E-mail inválido!' });
    } else {
      removeError('email');
    }
  }

  function handleCellphoneChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      celular: formatPhone(event.target.value),
    }));

    if (!event.target.value) {
      removeError('cellphone');
    } else if (formatPhone(event.target.value).length < 14) {
      setError({ field: 'cellphone', message: 'Digite um celular válido!' });
    } else {
      removeError('cellphone');
    }
  }

  function onLatChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      lat: value ? Number(onlyNumbers(value).toFixed(6)) : '',
    }));
  }

  function onLngChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      lng: value ? Number(onlyNumbers(value).toFixed(6)) : '',
    }));
  }

  function onCepChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      cep: value,
    }));
  }

  function onStreetNameChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      logradouro: value,
    }));
  }

  function onNumberChange(value: string | number) {
    setNewConsultData((prevState) => ({
      ...prevState,
      num: Number(value) || ''
    }));
  }

  function onDistrictChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      bairro: value
    }));
  }

  function onCityChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      cidade: value
    }));
  }

  function onUfChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      uf: value
    }));
  }

  function handleSelectedWorkplaceChange(wp: IWorkplacesOptions) {
    setNewConsultData((prevState) => ({
      ...prevState,
      codEndA: Number(wp.value),
      valorTeto: formatCurrency(String(wp.valTetoEnd?.toFixed(2) || '0')),
      semTaxaVT: wp.semTaxaSPtrans,
      semIntegraVT: wp.semIntegrarRJ,
      ...(wp.distanciaEnd ? { distancia: wp.distanciaEnd } : {})
    }));
    if (wp.distanciaEnd) {
      setWalkDistance([500, wp.distanciaEnd]);
      setSelectedWalkDistance([500, wp.distanciaEnd]);
    }
  }

  const navigateToWorkplaceEditPage = (workplaceCode?: number) => {
    handleSelectedCompanyChange({ value: companyCode, label: companyName });
    navigate(`/workplaces/${workplaceCode || valueLabelByCorrespondingWorkplace.codEnd}?returnLink=/consults/new`);
  };

  const handleWalkDistanceSelection = useCallback((km: number[]) => {
    setWalkDistance(km);
    setNewConsultData((prevState) => ({
      ...prevState,
      distancia: km[1],
    }));
  }, [setNewConsultData]);

  function handleUsesMirroringChange(uses: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      usaEspelhamento: uses,
    }));
  }

  function handleUsesAffreightmentChange(uses: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      usaFretado: uses,
    }));
  }

  function handleCeilingValueChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      valorTeto: formatCurrency(event.target.value)
    }));

    if (!event.target.value) {
      setError({ field: 'ceilingValue', message: 'Defina um valor máximo de VT'});
    } else if ((!isValueValid(parseCurrencyStringToFloat(formatCurrency(event.target.value)), 15, 100))) {
      setError({ field: 'ceilingValue', message: `Valor deve ser entre ${formatCurrency('15,00')} e ${formatCurrency('100,00')}`});
    } else {
      removeError('ceilingValue');
    }
  }

  function handleInitialValueChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      valorVT: formatCurrency(event.target.value)
    }));

    if (!event.target.value) {
      setError({ field: 'initialValue', message: 'Defina um valor inicial de VT'});
    } else if ((!isValueValid(parseCurrencyStringToFloat(formatCurrency(event.target.value)), 3, 100))) {
      setError({ field: 'initialValue', message: `Valor deve ser entre ${formatCurrency('3,00')} e ${formatCurrency('100,00')}`});
    } else {
      removeError('initialValue');
    }
  }

  function handleUsesNoTaxTickets(uses: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      semTaxaVT: uses,
    }));
  }

  function handleRJIntegration(integrates: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      semIntegraVT: !integrates,
    }));
  }

  function handleEntryTimeChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setNewConsultData((prevState) => ({
      ...prevState,
      horaEntra: value
    }));

    if (!value) {
      setError({ field: 'entryTime', message: 'Horário de entrada é obrigatório'});
    } else {
      removeError('entryTime');
    }
  }

  function handleExitTimeChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setNewConsultData((prevState) => ({
      ...prevState,
      horaSai: value
    }));

    if (!value) {
      setError({ field: 'exitTime', message: 'Horário de saída é obrigatório'});
    } else {
      removeError('exitTime');
    }
  }

  function handleWorkDaysChange(event: ChangeEvent<HTMLInputElement>) {
    const onlyNumbersValue = onlyNumbers(event.target.value);

    setNewConsultData((prevState) => ({
      ...prevState,
      diasVT: onlyNumbersValue,
    }));

    if (!isValueValid(onlyNumbersValue, 1, 30)) {
      setError({ field: 'workDays', message: 'Turno deve estar entre 1 e 30 dias!' });
    } else {
      removeError('workDays');
    }
  }

  function handleIsHighSalaryChange(isHigh: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      salario: isHigh ? 5000 : 2000
    }));
  }

  function handleRouteNameChange(event: ChangeEvent<HTMLInputElement>) {
    setRouteName(event.target.value);

    if (!event.target.value) {
      setError({ field: 'routeName', message: 'Denominação da rota é obrigatório!' });
    } else {
      removeError('routeName');
    }
  }

  const isValidToConfirm = useMemo(() => {
    const { cpf, mail, nome, cep, logradouro, num, bairro, cidade, uf, lat, lng } = newConsultData;
    console.log(selectedWorkplaces);

    return (
      (errors.length === 0) && (cpf && mail && nome && cep && logradouro && num && bairro && cidade && uf && lat && lng) && ((consultType === 'default' && !!(newConsultData.codEndA)) || (!!routeName && selectedWorkplaces.length > 1))
    );
  }, [consultType, errors.length, newConsultData, routeName, selectedWorkplaces]);

  const requiredFieldsErrorsLabel: string[] = useMemo(() => {
    const { cpf, mail, nome, cep, logradouro, num, bairro, cidade, uf, lat, lng } = newConsultData;
    return [
      !cpf ? 'CPF' : '',
      !mail ? 'E-mail' : '',
      !nome ? 'Nome' : '',
      !logradouro ? 'Logradouro' : '',
      !num ? 'Número' : '',
      !bairro ? 'Bairro' : '',
      !cep ? 'CEP' : '',
      !cidade ? 'Cidade' : '',
      !uf ? 'UF' : '',
      (!lat || !lng) ? 'Coordenadas' : '',
      (consultType === 'default' && (!newConsultData.codEndA)) ? 'Local de Trabalho' : '',
      (!(consultType === 'default') && (!routeName)) ? 'Denominação da Rota' : '',
      (!(consultType === 'default') && (!(selectedWorkplaces.length > 1))) ? 'Pelo menos 2 Locais de Trabalho' : '',
    ].filter((error) => !!error);
  }, [newConsultData, consultType, routeName, selectedWorkplaces.length]);

  const sendForRouting = useCallback(async () => {
    const routingSequence = [];
    if (consultType === 'multi') {
      const firstElement = {
        codConsulta: 0,
        codMultiRotas: 0,
        codRota: 1,
        identificacaoRota: routeName,
        sequenciaTrajeto: 1,
        tipoRotaMR: 1,
        codEndLocTrabOrigem: '',
        codEndLocTrabDestino: String(selectedWorkplaces[0].codEndA),
      };
      routingSequence.push(firstElement);

      const restElements = selectedWorkplaces.map((wp, index) => ({
        codConsulta: 0,
        codMultiRotas: 0,
        codRota: 1,
        identificacaoRota: routeName,
        sequenciaTrajeto: index + 2,
        tipoRotaMR: (index === selectedWorkplaces.length - 1 ? 3 : 2),
        codEndLocTrabOrigem: String(wp.codEndA),
        codEndLocTrabDestino: index === selectedWorkplaces.length - 1 ? '' : String(selectedWorkplaces[index + 1].codEndA),
      }));
      routingSequence.push(...restElements);

      const isFirstElementValid = !!routingSequence[0].codEndLocTrabDestino;
      const isLastElementValid = !!routingSequence[routingSequence.length - 1].codEndLocTrabOrigem;
      const areMiddleElementsValid = routingSequence.slice(1, routingSequence.length - 1).every((x) => !!x.codEndLocTrabOrigem && !!x.codEndLocTrabDestino);
      const isSequenceValid = isFirstElementValid && isLastElementValid && areMiddleElementsValid;

      if (!isSequenceValid) {
        toast.error('Sequência de locais de trabalho inválida. Por favor, revise os locais selecionados.');
        return;
      }
    }

    consultType === 'default' ? await apiCall({
      apiToCall: ConsultsService.sendForRouting,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        codEmpresa: companyCode,
        tipoRot: 1,
        servico: 2,
        nomeArquivoImp: '',
        dadosRoteirizar: [{
          ...newConsultData,
          cpf: newConsultData.cpf.slice(0, 18),
          matricula: newConsultData.matricula.slice(0, 18),
          nome: newConsultData.nome.slice(0, 50),
          mail: newConsultData.mail.slice(0, 50),
          logradouro: newConsultData.logradouro.slice(0, 50),
          complemento: newConsultData.complemento.slice(0, 20),
          bairro: newConsultData.bairro.slice(0, 25),
          cidade: newConsultData.cidade.slice(0, 30),
          gestaoSaldo: false,
          codEndAlpha: '',
          nomeArquivoImp: '',
          valorTeto: (typeof newConsultData.valorTeto === 'string' ? parseCurrencyStringToFloat(newConsultData.valorTeto) : newConsultData.valorTeto),
          valorVT: (typeof newConsultData.valorVT === 'string' ? parseCurrencyStringToFloat(newConsultData.valorVT) : newConsultData.valorVT),
          opcaoVTFreta: correspondingWorkplace?.opcaoVTFreta || false,
          temLinhasBias: false,
          temOpBias: false,
          temLinhasExcecao: false,
          temOpExcecao: false,
          linhasExcecao: null,
          opExcecao: null,
          linhasBias: null,
          opBias: null
        }],
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível roteirizar. Por favor, tente novamente (${response.msg})`);
          return;
        }

        const consultCodeAtResponse = response.codConsulta;
        navigate(`/consults/result/${consultCodeAtResponse}?codEmpresa=${companyCode}&activeSidebar=NewConsult&returnLink=${encodeURIComponent('/consults/new')}`);
      }
    }) : await apiCall({
      apiToCall: ConsultsService.sendForMultiRouting,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        codEmpresa: companyCode,
        tipoRot: 5,
        servico: 2,
        nomeArquivoImp: '',
        dadosRoteirizarMR: [{
          ...newConsultData,
          cpf: newConsultData.cpf.slice(0, 18),
          matricula: newConsultData.matricula.slice(0, 18),
          nome: newConsultData.nome.slice(0, 50),
          mail: newConsultData.mail.slice(0, 50),
          logradouro: newConsultData.logradouro.slice(0, 50),
          complemento: newConsultData.complemento.slice(0, 20),
          bairro: newConsultData.bairro.slice(0, 25),
          cidade: newConsultData.cidade.slice(0, 30),
          codEndA: selectedWorkplaces[0].codEndA,
          codEndAlpha: selectedWorkplaces[0].codEndNoclienteAlfaN,
          reprocessaTodos: false,
          rotaRelacionada: 0,
          gestaoSaldo: false,
          nomeArquivoImp: '',
          valorVTIdeal: 0,
          valorTeto: 0,
          valorVT: (typeof newConsultData.valorVT === 'string' ? parseCurrencyStringToFloat(newConsultData.valorVT) : newConsultData.valorVT),
          usaEspelhamento: false,
          usaFretado: false,
          rotasMR: routingSequence,
          rotasMRArq: [],
          opcaoVTFreta: correspondingWorkplace?.opcaoVTFreta || false,
        }],
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível roteirizar. Por favor, tente novamente (${response.msg})`);
          return;
        }

        const consultCodeAtResponse = response.codConsulta;
        navigate(`/consults/result/${consultCodeAtResponse}?codEmpresa=${companyCode}&activeSidebar=NewConsult&returnLink=${encodeURIComponent('/consults/new')}&mr`);
      }
    });
  }, [apiCall, companyCode, consultType, correspondingWorkplace?.opcaoVTFreta, navigate, newConsultData, routeName, selectedWorkplaces, userCode, userName, userProfile]);

  return {
    userProfile,
    userCode,
    userName,
    companyCode,
    isLoading,
    setIsLoading,
    getErrorMessageByFieldName,
    newConsultData,
    handleRegistrationChange,
    handleNameChange,
    handleEmailChange,
    handleCellphoneChange,
    newCoords,
    onLatChange,
    onLngChange,
    onCepChange,
    onStreetNameChange,
    onNumberChange,
    onDistrictChange,
    onCityChange,
    onUfChange,
    setError,
    removeError,
    valueLabelByCorrespondingWorkplace,
    workplacesOptions,
    handleSelectedWorkplaceChange,
    selectedTheme,
    correspondingWorkplace,
    navigateToWorkplaceEditPage,
    handleCpfChange,
    isValidToConfirm,
    hasError,
    handleTryAgain,
    handleUsesMirroringChange,
    handleUsesAffreightmentChange,
    selectedWalkDistance,
    handleWalkDistanceSelection,
    setSelectedWalkDistance,
    handleCeilingValueChange,
    handleInitialValueChange,
    handleUsesNoTaxTickets,
    handleRJIntegration,
    handleEntryTimeChange,
    handleExitTimeChange,
    handleWorkDaysChange,
    handleIsHighSalaryChange,
    sendForRouting,
    canChooseUsesAffreightment,
    consultType,
    handleConsultTypeChange,
    selectedWorkplaces,
    setSelectedWorkplaces,
    routeName,
    handleRouteNameChange,
    requiredFieldsErrorsLabel,
    isGettingMyPlace,
    hasMyPlace,
    asksGoToExistingResultModalData,
    setAsksGoToExistingResultModalData,
  };
}
